import React, { useEffect, useState } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import WelcomeSection from './components/WelcomeSection';
import MetricCard from './components/MetricCard';
import RecommendationCard from './components/RecommendationCard';
import ProgressTracker from './components/ProgressTracker';
import QuickActionCard from './components/QuickActionCard';
import AchievementBadge from './components/AchievementBadge';
import ActivityItem from './components/ActivityItem';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getUserQuizResults, getUserAchievements, getUserProgressReports } from '../../services/userService';
import { supabase } from '../../lib/supabase';

const StudentDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    quizResults: [],
    achievements: [],
    progressReports: []
  });
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      // Wait for auth to complete
      if (authLoading) return;
      
      setLoading(true);
      
      // If no user after auth completes, stop loading
      if (!user?.id) {
        setLoading(false);
        return;
      }
      
      try {
        // Fetch user points
        const { data: profileData, error: profileError } = await supabase?.from('user_profiles')?.select('points')?.eq('auth_user_id', user?.id)?.single();

        if (profileError && !profileError?.message?.includes('PGRST116')) {
          console.error('Error fetching user profile:', profileError);
        }

        // Count explored fields (quiz_completed = true)
        const { count: exploredCount, error: exploredError } = await supabase?.from('field_progress')?.select('*', { count: 'exact', head: true })?.eq('user_id', user?.id)?.eq('quiz_completed', true);

        if (exploredError) {
          console.error('Error counting explored fields:', exploredError);
        }

        // Count validated fields (quiz_validated = true)
        const { count: validatedCount, error: validatedError } = await supabase?.from('field_progress')?.select('*', { count: 'exact', head: true })?.eq('user_id', user?.id)?.eq('quiz_validated', true);

        if (validatedError) {
          console.error('Error counting validated fields:', validatedError);
        }

        // Count recommendations
        const { count: recommendedCount, error: recommendedError } = await supabase?.from('user_recommendations')?.select('*', { count: 'exact', head: true })?.eq('user_id', user?.id);

        if (recommendedError) {
          console.error('Error counting recommendations:', recommendedError);
        }

        // Fetch latest 3 achievements
        const { data: achievementsData, error: achievementsError } = await supabase?.from('achievements')?.select('id, achievement_id, achievement_title, icon, earned_at')?.eq('user_id', user?.id)?.order('earned_at', { ascending: false })?.limit(3);

        if (achievementsError) {
          console.error('Error fetching achievements:', achievementsError);
        }

        const [quizResults, achievements, progressReports] = await Promise.all([
          getUserQuizResults(user?.id),
          getUserAchievements(user?.id),
          getUserProgressReports(user?.id)
        ]);

        setDashboardData(prev => ({
          ...prev,
          quizResults: quizResults?.data || [],
          achievements: achievements?.data || [],
          progressReports: progressReports?.data || [],
          points: profileData?.points ?? prev?.points ?? 0,
          exploredCount: exploredCount || 0,
          validatedCount: validatedCount || 0,
          recommendedCount: recommendedCount || 0,
          latestBadges: achievementsData || []
        }));

        // Fetch latest 5 activities merged from quiz_results, achievements, field_progress
        const [qrResult, achResult, fpResult] = await Promise.all([
          supabase?.from('quiz_results')?.select('id, quiz_title, score, points_awarded, completed_at')?.eq('user_id', user?.id)?.order('completed_at', { ascending: false })?.limit(5),
          supabase?.from('achievements')?.select('id, achievement_title, points, earned_at')?.eq('user_id', user?.id)?.order('earned_at', { ascending: false })?.limit(5),
          supabase?.from('field_progress')?.select('id, field_id, last_points_awarded, completed_at')?.eq('user_id', user?.id)?.eq('quiz_completed', true)?.order('completed_at', { ascending: false })?.limit(5)
        ]);

        const qrData = qrResult?.data;
        const achData = achResult?.data;
        const fpData = fpResult?.data;

        const normalized = [
          ...(qrData || [])?.map(r => ({ type: 'quiz', title: `Quiz: ${r?.quiz_title || 'Quiz'}`, points: r?.points_awarded ?? Math.round((r?.score || 0) * 1.5), date: new Date(r?.completed_at) })),
          ...(achData || [])?.map(a => ({ type: 'achievement', title: `Badge: ${a?.achievement_title}`, points: a?.points ?? null, date: new Date(a?.earned_at) })),
          ...(fpData || [])?.map(f => ({ type: 'progress', title: `Filière explorée`, points: f?.last_points_awarded ?? null, date: new Date(f?.completed_at) }))
        ]?.sort((a, b) => b?.date - a?.date)?.slice(0, 5);

        setRecentActivities(normalized);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user?.id, authLoading]);

  const studentData = {
    name: userProfile?.full_name || "Étudiant",
    level: `Niveau ${Math.min(Math.floor(dashboardData?.achievements?.length / 2) + 1, 5)} - ${dashboardData?.achievements?.length > 10 ? 'Expert' : dashboardData?.achievements?.length > 5 ? 'Explorateur' : 'Débutant'}`,
    progressPercentage: Math.min(Math.round((dashboardData?.quizResults?.length / 15) * 60 + (dashboardData?.achievements?.length / 12) * 40), 100)
  };

  const totalPoints = dashboardData?.points || 0;

  const metrics = [
    {
      icon: "Lightbulb",
      title: "Filières recommandées",
      value: (dashboardData?.recommendedCount || 0)?.toString(),
      subtitle: "Suggestions personnalisées",
      color: "blue",
      trend: 12
    },
    {
      icon: "Compass",
      title: "Filières explorées",
      value: (dashboardData?.exploredCount || 0)?.toString(),
      subtitle: "Quiz complétés",
      color: "green",
      trend: 8
    },
    {
      icon: "Target",
      title: "Filières validées",
      value: (dashboardData?.validatedCount || 0)?.toString(),
      subtitle: "Score ≥ 60%",
      color: "amber",
      trend: 15
    },
    {
      icon: "Award",
      title: "Points",
      value: totalPoints?.toString(),
      subtitle: `Niveau ${Math.min(Math.floor(dashboardData?.achievements?.length / 2) + 1, 5)} atteint`,
      color: "purple",
      trend: 20
    }
  ];

  const recommendations = dashboardData?.progressReports?.slice(0, 3)?.map((report, index) => ({
    id: report?.id,
    title: report?.report_title,
    category: report?.category,
    description: `Basé sur vos résultats avec un score de ${report?.score}%`,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_136076b03-1767113315595.png",
    imageAlt: `Illustration pour ${report?.report_title}`,
    icon: index === 0 ? "Cpu" : index === 1 ? "TrendingUp" : "Heart",
    matchScore: report?.score,
    tags: [report?.category]
  }));

  const progressCategories = dashboardData?.progressReports?.slice(0, 5)?.map((report, index) => ({
    name: report?.report_title,
    description: report?.category,
    score: report?.score,
    color: ["var(--color-primary)", "var(--color-success)", "var(--color-accent)", "var(--color-secondary)", "var(--color-warning)"]?.[index % 5]
  }));

  const quickActions = [
    {
      title: "Chat IA",
      description: "Discutez avec notre assistant pour obtenir des recommandations personnalisées",
      icon: "MessageSquare",
      color: "blue",
      path: "/ai-chat-interface"
    },
    {
      title: "Nouveau Quiz",
      description: "Testez vos aptitudes avec nos quiz interactifs",
      icon: "ClipboardList",
      color: "green",
      path: "/interactive-quiz-system"
    },
    {
      title: "Explorer les Filières",
      description: "Découvrez toutes les filières disponibles et leurs débouchés",
      icon: "Compass",
      color: "amber",
      path: "/study-field-results"
    },
    {
      title: "Annuaire des Écoles",
      description: "Trouvez les établissements qui correspondent à vos ambitions",
      icon: "School",
      color: "purple",
      path: "/landing-page"
    }
  ];

  const badges = dashboardData?.achievements?.map((ach, index) => ({
    id: ach?.id,
    name: ach?.achievement_title,
    icon: ach?.icon || ["Footprints", "Compass", "Brain", "Eye", "Target", "Zap", "Award", "Trophy"]?.[index % 8],
    earned: true,
    earnedDate: new Date(ach.earned_at)?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
  }));

  const activities = recentActivities?.map((item, index) => ({
    id: `${item?.type}-${index}`,
    type: item?.type,
    title: item?.title,
    description: item?.points != null ? `+${item?.points} points` : '',
    timestamp: item?.date,
    points: item?.points
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <main className="content-area">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Chargement de votre tableau de bord...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <main className="content-area">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <WelcomeSection
            studentName={studentData?.name}
            currentLevel={studentData?.level}
            progressPercentage={studentData?.progressPercentage}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 lg:gap-6 mt-6 md:mt-8 lg:mt-10">
            {metrics?.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </div>

          {dashboardData?.latestBadges?.length > 0 && (
            <div className="mt-8 md:mt-10 lg:mt-12">
              <div className="flex items-center gap-3 mb-6">
                <Icon name="Award" size={24} className="text-primary" />
                <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                  Badges Récents
                </h2>
              </div>
              
              <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {dashboardData?.latestBadges?.map((badge) => (
                    <div key={badge?.id} className="flex items-center gap-3 p-3 bg-background rounded-lg">
                      <div className="text-3xl">{badge?.icon || '🏆'}</div>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">{badge?.achievement_title}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(badge?.earned_at)?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {recommendations?.length > 0 && (
            <div className="mt-8 md:mt-10 lg:mt-12">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Icon name="Lightbulb" size={24} className="text-primary" />
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                    Recommandations Récentes
                  </h2>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
                {recommendations?.map((recommendation) => (
                  <RecommendationCard key={recommendation?.id} recommendation={recommendation} />
                ))}
              </div>
            </div>
          )}

          {progressCategories?.length > 0 && (
            <div className="mt-8 md:mt-10 lg:mt-12">
              <ProgressTracker categories={progressCategories} />
            </div>
          )}

          <div className="mt-8 md:mt-10 lg:mt-12">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="Zap" size={24} className="text-primary" />
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                Actions Rapides
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 lg:gap-6">
              {quickActions?.map((action, index) => (
                <QuickActionCard key={index} action={action} />
              ))}
            </div>
          </div>

          <div className="mt-8 md:mt-10 lg:mt-12">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="Award" size={24} className="text-primary" />
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                Badges et Réalisations
              </h2>
            </div>
            
            <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 border border-border shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm md:text-base text-muted-foreground">Points Totaux</p>
                  <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">{totalPoints}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm md:text-base text-muted-foreground">Badges Gagnés</p>
                  <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">{badges?.length}/12</p>
                </div>
              </div>
              
              {badges?.length > 0 ? (
                <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 md:gap-6">
                  {badges?.map((badge) => (
                    <AchievementBadge key={badge?.id} badge={badge} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="Award" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucun badge gagné pour le moment. Complétez des quiz pour débloquer des badges!</p>
                </div>
              )}
            </div>
          </div>

          {activities?.length > 0 && (
            <div className="mt-8 md:mt-10 lg:mt-12">
              <div className="flex items-center gap-3 mb-6">
                <Icon name="Activity" size={24} className="text-primary" />
                <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                  Activité Récente
                </h2>
              </div>
              
              <div className="bg-card rounded-xl border border-border shadow-sm divide-y divide-border">
                {activities?.map((activity) => (
                  <ActivityItem key={activity?.id} activity={activity} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default StudentDashboard;